import React, { useCallback, useEffect, useState } from "react";
import { formatCurrency, formatNumber, money, toast, validarPINSeguridad } from "../../../utils/helpers";
import { auth } from "../../../utils/auth";
import { useChecks } from "../../Ingresos/Hooks/useChecks";
import {
    normalizarUnidades,
	construirOpcionUnidad,
	opcionUnidadBase,
	escalarPreciosPorUnidad,
	cantidadMaximaEnUnidad,
	filaUnidadDeLinea,
	aCantidadBase,
	precioPorUnidad,
	reetiquetarPrecio,
	usaPrecioPaquete, 
} from "../../Ingresos/Hooks/useUnidadLinea";
import { apiRequest } from "../../../hooks/apiRequest";

const calcularDescuento = (costo, descuento) =>
	money(costo * descuento / 100);

// El impuesto de una línea llega del backend como {id, nombre}, pero
// `updateTotal` lo reescribe con la forma del dropdown ({value, label}).
const aOpcionImpuesto = (impuesto) => ({
	label: impuesto?.nombre ?? impuesto?.label,
	value: impuesto?.id ?? impuesto?.value,
	porcentaje: impuesto?.porcentaje,
	tipo: impuesto?.tipo,
});

const useProducto = (producto, facturaProductos, updateTotal, index, impuestosForDropdown, banderaContacto, precioLimite, contacto, isEdit, isInterna = false) => {

    const [productoInfo, setProductoInfo] = useState({
		id: false,
		nombre: false,
		cantidad: producto.cantidad,
		costo: isInterna ? producto.importe : producto.costo,
		descuento: producto.descuento,
        importe: producto.importe,
        precio_facturado: producto.precio_facturado,
		precios: [],
		impuesto: aOpcionImpuesto(producto.impuesto),
		precio_venta: null,
		existencia: null,
		cantidadAnterior: null,
		precioCompra: null,
		unidades: [],
		unidadSeleccionada: null,
		factor: 1,
	});

	const { checkProductoRepetido } = useChecks(
		facturaProductos,
		productoInfo
	);

	const [showFotos, setShowFotos] = useState(false);
	const [showDelete, setShowDelete] = useState(false);
	const [showPrecioManual, setShowPrecioManual] = useState(isInterna);
	const [showPINModal, setShowPINModal] = useState(false);
    const [showPrecioCompra, setShowPrecioCompra] = useState(false);

	const [pinSeguridad, setPinSeguridad] = useState("");
	const [precioManual, setPrecioManual] = useState(isInterna ? producto.precio_facturado : 0);
	const [pinSeguridadValidated, setPinSeguridadValidated] = useState(false);
    const [chequeoPrecio, setChequeoPrecio] = useState(true);

    const [isPrecioPorDebajo, setIsPrecioPorDebajo] = useState(false);

	const onChangeInput = (field, value) => {
		setProductoInfo((producto) => ({
			...producto,
			[field]: value,
		}));
	};

	const handlePrecioVentaChange = (precio) => {
		if (precio.value === "manual") {
			setShowPrecioManual(true);
			return;
		}

        const imp = impuestosForDropdown.filter(i => i.value === precio.impuesto_id).shift();

		setProductoInfo(prev => ({
			...prev,
			impuesto: imp,
			costo: calcularImporte(precio.value, imp),
			precio_venta: precio,
			descuento: 0,
		}));

        setPrecioManual(precio.value);
	};

    const onChangeImpuesto = (impuesto_id) => {
        const imp = impuestosForDropdown.find(i => i.value === impuesto_id);

        const precio = (productoInfo.costo * parseFloat(imp?.porcentaje + 1)).toFixed(2);

        setProductoInfo(prev => ({
            ...prev,
            precio_venta: {
                value: precio,
                label: `1 - ${formatNumber(precio)}`,
                impuesto_id: imp.id,
            },
            impuesto: imp,
        }));

        setPrecioManual(precio);
    }

	const onChangeProducto = (productoId, newProducto = {}) => {
		checkProductoRepetido(productoId, index);

		producto.producto_id = productoId;
		producto.productoSeleccionado = null;

        setShowPrecioManual(false);
        setPrecioManual('');

        let plength = precioLimite && newProducto?.precios.length >= precioLimite ? precioLimite : (precioLimite && precioLimite > newProducto?.precios.length ? newProducto?.precios.length : 1);
        plength = banderaContacto ? plength : 1;

        updateProducto(newProducto, plength - 1);
	};

	const productLabel = useCallback((producto) => {
		return (
			<span className="row px-0 align-items-center">
				<span
					className="col-2 pr-1"
					style={{ minWidth: "100px", wordBreak: "break-all" }}
				>
					{producto.referencia}
				</span>
				<span className="col-5 px-1">{producto.nombre}</span>
				<span
					className="col-1 px-1"
					style={{
						fontWeight: "bold",
						color: "#008aab",
					}}
				>
					{isNaN(parseFloat(producto.cantidad))
						? "∞"
						: parseFloat(producto.cantidad)}
				</span>
				<span
					className="col-2 pr-0"
					style={{
						fontWeight: "bold",
						color: "#008aab",
					}}
				>
					{banderaContacto &&
						precioLimite >= 1 &&
						producto.precios?.length >= precioLimite
							? formatCurrency(
									money(
										producto.precios[precioLimite - 1]
											?.total
									) || 0,
									"$"
							  )
							: formatCurrency(
									money(producto.precios[0]?.total) || 0,
									"$"
							  )}
				</span>
			</span>
		);
	}, [contacto]);

	const calcularItbis = (total) => {
		return total * (productoInfo.impuesto?.porcentaje || 0);
	};

    const volverAlSelector = async () => {
        const plength = banderaContacto && precioLimite ? precioLimite : 1;
        const precioOriginal = productoInfo?.precios[plength-1];
        const precioEnUnidad = precioPorUnidad(precioOriginal?.total, productoInfo.unidadSeleccionada, precioOriginal?.precio_paquete);
        setShowPrecioManual(false);

        handlePrecioVentaChange({
            label: `${plength} -> ${formatNumber(precioEnUnidad)}`,
            value: precioEnUnidad,
            valorBase: parseFloat(precioOriginal?.total),
            precio_paquete: precioOriginal?.precio_paquete ?? null,
            impuesto_id: precioOriginal.impuesto?.id,
            tipo: precioOriginal.impuesto?.tipo
        });

        setProductoInfo(prev => {
            const newProductoInfo = {
                ...prev,
                impuesto: {
                    label: precioOriginal?.impuesto?.nombre,
                    value: precioOriginal?.impuesto?.id,
                    porcentaje: precioOriginal?.impuesto?.porcentaje,
                    tipo: precioOriginal?.impuesto?.tipo,
                },
                precio_manual: precioEnUnidad
            }

            onChangeCostoManual(precioEnUnidad, false, newProductoInfo);

            return newProductoInfo;
        });
    }

    const preciosVentaBase = useCallback(() => {
		if (!precioLimite && !banderaContacto) {
			return productoInfo.precios?.map((precio, index) => ({
				value: parseFloat(precio.total),
				label: `${index + 1} -> ${formatNumber(precio.total)}`,
				impuesto_id: precio.impuesto?.id,
					precio_paquete: precio.precio_paquete ?? null,
			}));
		}

		let precios = [];

		if (productoInfo.precios?.length > 0 && !banderaContacto) {
            productoInfo.precios.forEach((precio, index) => {

				if (index + 1 <= precioLimite) {
					precios.push({
						value: parseFloat(precio.total),
						label: `${index + 1} -> ${formatNumber(precio.total)}`,
						impuesto_id: precio.impuesto?.id,
					precio_paquete: precio.precio_paquete ?? null,
					});
				}
			});
		} else if (productoInfo.precios?.length > 0 && banderaContacto) {
            if (precioLimite > productoInfo.precios?.length) {
                productoInfo.precios.forEach((precio, index) => {
                    if (index + 1 === productoInfo.precios.length) {
                        precios.push({
                            value: parseFloat(precio.total),
                            label: `${index + 1} -> ${formatNumber(precio.total)}`,
                            impuesto_id: precio.impuesto?.id,
					precio_paquete: precio.precio_paquete ?? null,
                        });
                    }
                });
            } else {
                productoInfo.precios.forEach((precio, index) => {
                    if (index + 1 >= precioLimite) {
                        precios.push({
                            value: parseFloat(precio.total),
                            label: `${index + 1} -> ${formatNumber(precio.total)}`,
                            impuesto_id: precio.impuesto?.id,
					precio_paquete: precio.precio_paquete ?? null,
                        });
                    }
                });
            }
		}
		return precios;
	}, [productoInfo.precios, precioLimite, banderaContacto]);

	// Cada opción de precio escalada por la unidad de la línea (factor × precio
	// de lista, o el precio propio de la unidad si lo tiene). `valorBase` queda
	// disponible para re-escalar sin arrastrar el factor anterior.
	const preciosVenta = useCallback(() => {
		return escalarPreciosPorUnidad(preciosVentaBase(), productoInfo.unidadSeleccionada);
	}, [preciosVentaBase, productoInfo.unidadSeleccionada]);

	const onChangeUnidad = (nuevaUnidad) => {
		setProductoInfo((prev) => {
			const siguiente = {
				...prev,
				unidadSeleccionada: nuevaUnidad,
				factor: nuevaUnidad?.factor ?? 1,
			};

			// El precio manual lo escribió el usuario en la unidad anterior; no
			// se re-escala solo, para no cambiarle a la fuerza lo que tecleó.
			if (!showPrecioManual && prev.precio_venta?.valorBase != null) {
				const nuevoValor = precioPorUnidad(prev.precio_venta.valorBase, nuevaUnidad, prev.precio_venta.precio_paquete);
				siguiente.precio_venta = {
					...prev.precio_venta,
					value: nuevoValor,
					label: reetiquetarPrecio(prev.precio_venta.label, nuevoValor),
				};
				siguiente.costo = calcularImporte(nuevoValor, prev.impuesto);
			}

			return siguiente;
		});
	};

	const toggleFotosModal = () => {
		setShowFotos((state) => !state);
	};

	const toggleDeleteModal = () => {
		setShowDelete((state) => !state);
	};

	const onChangeCostoManual = async (value, revisarCalculo = false, productoOriginal = null) => {
        const precios = preciosVenta().length > 0 ? preciosVenta() : productoOriginal?.precios;
        const descuento = productoInfo.descuento ? value * productoInfo.descuento / 100 : 0;

        if (isInterna) {
            onChangeInput("costo", calcularImporte(value, productoInfo.impuesto));
            return;
        }

        let plength = (precioLimite && precios?.length >= precioLimite) ? precioLimite : precios?.length;
        plength = banderaContacto ? precios?.length : plength;
        const precioVenta = precios ? precios[plength-1] : { value: value + 100};
        const precioMinimo = Math.min(...precios.map(p => p.value));

        let imp = null;

        if ( productoOriginal ) {
            imp = productoOriginal.impuesto;
        } else if (showPrecioManual ) {
            imp = productoInfo.impuesto;
        } else {
            imp = impuestosForDropdown?.find(
                i => i.value === precioVenta?.impuesto_id
            );
        }

		let menorPrecioVenta = calcularImporte(precioVenta?.value || precioVenta?.total, imp);

		const importeCalculado = calcularImporte(value || precioManual, imp);

        if (revisarCalculo && importeCalculado - descuento < (productoInfo.precioCompra || 0) * (productoInfo.factor || 1)) {
            toast("ATENCIÓN: Este precio es menor al costo de compra.", "error");
        }

		if (revisarCalculo && value - descuento < precioMinimo) {
			if (pinSeguridadValidated) {
				onChangeInput("costo", importeCalculado);
				return;
			}

            setIsPrecioPorDebajo(true);

			setShowPINModal(true);
			return;
		}

        onChangeInput("costo", importeCalculado);
	};

	const productoITBIS = calcularItbis(productoInfo.costo);
    const precioVenta = parseFloat(productoInfo?.precio_venta?.value);

    const st = !showPrecioManual
		? ((precioVenta?.toFixed(2)) * productoInfo.cantidad) ||
		  0
		: precioManual * productoInfo.cantidad;

	// const totalItbis = calcularItbis(costoTotal);
	const totalItbis =
    calcularItbis(productoInfo.costo).toFixed(2) * productoInfo.cantidad;

	const costoTotal = st - totalItbis;

	const desc =
		calcularDescuento(costoTotal + totalItbis, productoInfo.descuento) || 0;

	const subtotal = costoTotal || 0;

    const costoReal = (productoInfo.costo - (productoInfo.costo * productoInfo.descuento / 100)) * productoInfo.cantidad;
    const descuentoReal = calcularDescuento(productoInfo.costo * productoInfo.cantidad, productoInfo.descuento);
    const itbisReal = calcularItbis(costoReal);
    const totalReal = costoReal + itbisReal;

	const total = st;

	//Cargar la informacion del producto desde factura existente o autoguardado
	const changeProductoToEdit = (productoOriginal = {}) => {
        const productoId = productoOriginal.id;
        const precio_facturado = productoOriginal.precio_facturado;

        if (!productoId) {
            setShowPrecioManual(true);
            onChangeCostoManual(precio_facturado);
            setPrecioManual(precio_facturado);
            return;
        }

		const newProducto = {...productoOriginal};

        // La línea trae el snapshot de su unidad: `unidad_id` desde el
        // autoguardado, o sólo `unidad` (nombre) + `factor` desde el backend,
        // que no guarda la FK en el detalle. Sin coincidencia cae en la unidad
        // base y el comportamiento es idéntico al de antes de la Fase 2.
        const filasUnidad = normalizarUnidades(newProducto);
        const filaUnidadOriginal = filaUnidadDeLinea(filasUnidad, productoOriginal);
        const opcionUnidadOriginal = filaUnidadOriginal
            ? construirOpcionUnidad(filaUnidadOriginal)
            : null;
        // El precio se guardó con el factor del snapshot, no con el actual.
        const factorOriginal = Number(productoOriginal.factor) || opcionUnidadOriginal?.factor || 1;

        // El precio guardado está en la unidad de la línea. Para ubicar cuál
        // lista de precio coincide se compara contra su precio de paquete si la
        // línea es de empaque y la lista lo tiene; si no, contra el precio de
        // la lista, volviendo a la unidad base con el factor del snapshot.
        let precioIndex = 0;
        precioIndex = newProducto.precios?.findIndex((precio) =>
            usaPrecioPaquete(opcionUnidadOriginal, precio.precio_paquete)
                ? parseFloat(precio.precio_paquete) === parseFloat(precio_facturado)
                : parseFloat(precio.precio) === precio_facturado / factorOriginal
        );

        if (precioIndex < 0) {
            setShowPrecioManual(true);
            updateProducto(newProducto, precioIndex, productoOriginal, opcionUnidadOriginal);
            setPrecioManual(precio_facturado);

            //Checkeo de precios al cargar
            if(
                chequeoPrecio
                && precio_facturado > 0
                && !isEdit
            ) {
                onChangeCostoManual(precio_facturado, true, newProducto);
                setChequeoPrecio(false);
            }
            return;
        }

        updateProducto(newProducto, precioIndex, productoOriginal, opcionUnidadOriginal);
	};

    const updateProducto = (newProducto, precioIndex, productoOriginal = null, unidadOverride = null) => {
        let newPrecio = null;

        if (precioIndex >= 0) {
            newPrecio = newProducto?.precios[precioIndex];
        }

        // Antes del fallback: sólo un precio de lista viene en unidad base y se
        // escala; el fallback (precio_facturado de la línea) ya está en la
        // unidad de la línea y escalarlo otra vez inflaría costo e ITBIS.
        const precioDeLista = Boolean(newPrecio);

        //Usar el impuesto del primer precio de venta
        let newimpuesto = {};

        if (productoOriginal) {
            newimpuesto = productoOriginal.impuesto;
        } else {
            newimpuesto = newProducto.precios ? newProducto.precios[0]?.impuesto : newProducto.impuesto;
        }

        if (!newPrecio) {
            newPrecio = {
                precio: productoInfo.precio_facturado,
                precio_venta: calcularImporte(productoInfo.precio_facturado, newimpuesto),
                impuesto: newimpuesto
            };
        }

        const filasUnidad = normalizarUnidades(newProducto);
        const opcionUnidad = unidadOverride || opcionUnidadBase(newProducto);
        const precioEscalado = precioDeLista
            ? precioPorUnidad(parseFloat(newPrecio?.precio || 0), opcionUnidad, newPrecio?.precio_paquete)
            : parseFloat(newPrecio?.precio || 0);
        // El precio de paquete de la lista trae impuesto incluido: el costo se
        // deriva de él, no de precio_venta × factor.
        const costoEscalado = !precioDeLista
            ? newPrecio?.precio_venta
            : usaPrecioPaquete(opcionUnidad, newPrecio?.precio_paquete)
                ? calcularImporte(precioEscalado, newimpuesto)
                : precioPorUnidad(newPrecio?.precio_venta, opcionUnidad);

        setProductoInfo((state) => ({
            ...state,
            id: newProducto.id,
            nombre: (
                <>
                    <span
                        className="col-3 pl-1"
                        style={{
                            minWidth: "100px",
                            wordBreak: "break-all !important",
                        }}
                    >
                        {newProducto.referencia}
                    </span>{" "}
                    <span className="col-8">{newProducto.nombre}</span>
                </>
            ) || '',
            importe: newProducto.importe,
            costo: costoEscalado,
            precio_facturado: newProducto.precio_facturado || newPrecio.precio,
            referencia: newProducto.referencia,
            unidad: opcionUnidad.value,
            unidades: filasUnidad,
            unidadSeleccionada: opcionUnidad,
            factor: opcionUnidad.factor,
            precios: newProducto.precios || [],
            fotos: newProducto.fotos
                ? newProducto.fotos.map((foto) => foto.url)
                : [],
            precio_venta: {
                value: precioEscalado,
                valorBase: precioDeLista ? parseFloat(newPrecio?.precio || 0) : null,
                precio_paquete: precioDeLista ? newPrecio?.precio_paquete ?? null : null,
                label: `${precioIndex + 1} -> ${formatNumber(precioEscalado)}`,
                impuesto_id: aOpcionImpuesto(newPrecio.impuesto).value,
            },
            impuesto: aOpcionImpuesto(newimpuesto),
            existencia: newProducto.cantidad,
            cantidadAnterior: newProducto.cantidad,
            precioCompra: newProducto.costo,
        }));
    }

    const checkQuantityConstrains = () => {
        // existencia viene en unidad base; la cantidad, en la unidad de la línea.
        const factor = productoInfo.factor || 1;
        const avisarSinUnidadCompleta = (maximo) => {
            if (maximo <= 0 && factor > 1) {
                toast(`No hay una ${productoInfo.unidadSeleccionada?.value} completa disponible`, "error");
            }
        };

        if (isEdit) {
            const existencia = productoInfo.existencia + productoInfo.cantidadAnterior
			if (
				productoInfo.existencia != null &&
				productoInfo.cantidadAnterior != null &&
				aCantidadBase(productoInfo.cantidad, factor) > existencia
			) {
				const maximo = cantidadMaximaEnUnidad(existencia, factor);
				toast(
					`Existencia Actual ${
						existencia
					}`,
					"error"
				);
				avisarSinUnidadCompleta(maximo);
				onChangeInput(
					"cantidad",
					maximo
				);
                return;
			}
            if (productoInfo.cantidadAnterior) {
                return;
            }
		}

        if (
			productoInfo.existencia != null &&
			aCantidadBase(producto.cantidad, factor) > productoInfo.existencia
		) {
			const maximo = cantidadMaximaEnUnidad(productoInfo.existencia, factor);
			toast(`Existencia Actual ${productoInfo.existencia}`, "error");
			avisarSinUnidadCompleta(maximo);
			producto.cantidad = maximo;
			onChangeInput("cantidad", maximo);
			return;
		}

		if (producto.cantidad <= 0) {
			toast(`Cantidad Debe ser mayor a 0`, "error");
			producto.cantidad = 1;
			onChangeInput("cantidad", 1);
			return;
		}
	};

    const getProductos = async (url) => {
        const table = await apiRequest({}, url, 'get');
        if (table.data) {
            const almacenesGuardados = JSON.parse(localStorage.getItem("almacenes"));
            return table.data.filter((producto) => {
                if (!auth.esVendedor()) return true;
                return auth.almacenes().includes(producto.almacen?.id);
            }).map(item => {
                return {
                    value: item.id,
                    label: item.nombre,
                    producto: item
                };
            });
        } else {
            return [{label: "No se encontraron productos", value: null}];
        }
    };

	const checkPINSeguridad = async () => {
		if (await validarPINSeguridad(pinSeguridad)) {
            proceedWithPin();
			return;
		}

		toast("PIN incorrecto, intente de nuevo", "error");
	};

    const proceedWithPin = (proceed = false) => {
        if (!proceed.detail) return;

        setPinSeguridad("");
        setShowPINModal(false);
        setPinSeguridadValidated(true);
    }

	const togglePINModal = () => {
		setShowPINModal(false);

        if (isPrecioPorDebajo) {
            const plength = banderaContacto && precioLimite ? precioLimite : 1;

            const precioLista = productoInfo.precios[plength-1];
            const nuevoPrecio = productoInfo.precio_venta?.value || precioPorUnidad(money(precioLista?.precio), productoInfo.unidadSeleccionada, precioLista?.precio_paquete);
            const importeCalculado =  calcularImporte(nuevoPrecio, productoInfo.impuesto);

            onChangeInput("costo", importeCalculado);
            onChangeInput("descuento", 0);
            setPrecioManual(nuevoPrecio);
        }
	};

    const calcularImporte = (precio, impuesto) => {
        return parseFloat(precio) / (parseFloat(impuesto?.porcentaje) + 1);
    }

	useEffect(() => {
		if (!producto.producto?.id ||!producto.precio_facturado) {
            return;
        }
        changeProductoToEdit(
            producto
        );
	}, [producto.producto?.id]);

    //Esto Actualiza la cantidad luego de chekear productos duplicados
	useEffect(() => {
		onChangeInput("cantidad", producto.cantidad);
	}, [producto.cantidad]);

	useEffect(() => {
        changeProductoToEdit(producto);
	}, [isInterna]);

	useEffect(() => {
		updateTotal(
			producto.id,
			desc > 0 ? costoReal : subtotal,
			desc > 0 ? totalReal : total,
			desc > 0 ? itbisReal : totalItbis,
			desc > 0 ? descuentoReal : 0,
			productoInfo.impuesto,
            productoInfo
		);
	}, [subtotal, totalItbis, total, desc, showPrecioManual, productoInfo.unidadSeleccionada]);

    useEffect(() => {
        window.addEventListener('pinValidado', proceedWithPin);
        window.addEventListener('pinCancelado', togglePINModal);
        return () => {
            window.removeEventListener('pinValidado', proceedWithPin);
            window.removeEventListener('pinValidado', togglePINModal);
        }
    }, [togglePINModal, proceedWithPin]);

    return {
        productoInfo,
        productLabel,
        getProductos,
        total,
        totalReal,
        preciosVenta,
        precioManual,
        pinSeguridad,
        productoITBIS,
        checkPINSeguridad,
        onChangeInput,
        onChangeImpuesto,
        onChangeProducto,
        onChangeCostoManual,
        onChangeUnidad,
        handlePrecioVentaChange,
        setProductoInfo,
        setPrecioManual,
        setPinSeguridad,
        setShowPrecioManual,
        setShowFotos,
        setShowPrecioCompra,
        toggleFotosModal,
        toggleDeleteModal,
        togglePINModal,
        showPrecioManual,
        showFotos,
        showDelete,
        showPINModal,
        showPrecioCompra,
        volverAlSelector,
        checkQuantityConstrains
    }
}

export default useProducto;
